import { FaFacebookF } from '@react-icons/all-files/fa/FaFacebookF';
import { FaInstagram } from '@react-icons/all-files/fa/FaInstagram';
import { FaYoutube } from '@react-icons/all-files/fa/FaYoutube';
import React, { useContext } from 'react';

import ColorContext from '../../context/ColorContext';
import groupBy from '../../utils/helpers/groupBy';
import { getCorrectLink } from '../../utils/links';
import { Box, Flex, Grid, Stack } from '../Core';
import StyledLink from '../StyledLink';
import Text from '../Text';
import { TEXT_STYLES } from '../Text/constants';
import { addMenuItemsFromContentItems } from './utils';

declare let window: CustomWindow;

interface IFooterProps {
  currentLanguageCode: string;
  menuItems: MenuItem[];
  siteSettings: SiteConfiguration;
  submenuItems: IPageContext['submenuItems'];
}

const Footer: FCWithClassName<IFooterProps> = (props) => {
  const { theme } = useContext(ColorContext);
  const color = theme === 'institute' ? 'white' : 'text';
  const menuItemsGroups = props.menuItems?.map((menuItem) => {
    let menuGroup: MenuItem;
    if (menuItem.title === 'blog') {
      const categories = props.submenuItems.blogCategories;
      const blogCategoriesMenuGroup = addMenuItemsFromContentItems({
        contentItems: categories,
        menuGroup: menuItem,
      });
      const blogCategoriesMenuGroupWithQueryLink: MenuItem = {
        ...blogCategoriesMenuGroup,
        childItems: {
          nodes: blogCategoriesMenuGroup.childItems.nodes.map(
            (childItem: MenuItem['childItems']['nodes'][number], index: number) => {
              if (!childItem) return undefined;
              return {
                ...childItem,
                // TODO : this is hacky, we should test if this is the default language
                url: `${props.currentLanguageCode === 'EN' ? 'en/' : ''
                  }blog/?category=${categories[index]?.slug}`,
              };
            }
          ),
        },
      };
      menuGroup = blogCategoriesMenuGroupWithQueryLink;
    } else {
      menuGroup = menuItem;
    }
    const { socialLinks, otherLinks } = groupBy(
      menuGroup.childItems.nodes,
      (childItem) => {
        if (!childItem) return undefined;
        return ['FB', 'IG', 'YT'].includes(childItem.label)
          ? 'socialLinks'
          : 'otherLinks';
      }
    );
    const serializedLinks = otherLinks?.map((childItem) => {
      if (!childItem) return undefined;
      const styledLinkDestination: {
        href?: string;
        to?: string;
      } = getCorrectLink(childItem.url);
      return (
        <StyledLink
          key={childItem.id}
          variant="navigationLink"
          target={childItem.target ?? undefined}
          // color={color}
          className={`!normal-case !w-48 ${color === 'white' ? 'text-white' : 'text-text'
            }`}
          {...styledLinkDestination}
        >
          {childItem.label}
        </StyledLink>
      );
    });
    const serializedSocialLinks = (
      <Stack isInline align="center" className="w-48 space-x-2">
        {socialLinks?.map((socialLink) => {
          if (!socialLink) return undefined;
          if (socialLink.label === 'FB') {
            return (
              <a
                key={socialLink.id}
                href={socialLink.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="border-b-0"
              >
                <FaFacebookF className={`w-6 h-6 text-${color}`} />
              </a>
            );
          } else if (socialLink.label === 'IG') {
            return (
              <a
                key={socialLink.id}
                href={socialLink.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="border-b-0"
              >
                <FaInstagram className={`w-6 h-6 text-${color}`} />
              </a>
            );
          } else if (socialLink.label === 'YT') {
            return (
              <a
                key={socialLink.id}
                href={socialLink.url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="border-b-0"
              >
                <FaYoutube className={`w-6 h-6 text-${color}`} />
              </a>
            );
          } else {
            return null;
          }
        })}
      </Stack>
    );
    return (
      <Stack
        className="space-y-2"
        key={menuGroup.id}
        align={{ base: 'flex-start', lg: 'center' }}
      >
        <Text variant={TEXT_STYLES.H4} style={{ color }} className="!w-48">
          {menuGroup.label}
        </Text>
        {serializedLinks}
        {serializedSocialLinks}
      </Stack>
    );
  });
  const subfooterElements = props.siteSettings.footer;
  const openCookiehubBanner = () => window.cookiehub.openSettings();
  const copyrightYear = `©${new Date().getFullYear()}`
  return (
    <Box
      className={
        theme == 'institute' ? 'bg-primaryInstitute' : 'bg-primaryMain'
      }
    >
      <Flex justify="center" className="w-full p-8 mx-auto max-w-screen-xxl">
        <Grid className="gap-y-16 w-full m-0 lg:m-auto grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
          {menuItemsGroups}
        </Grid>
      </Flex>
      <Flex
        className="flex-wrap mb-10 max-w-[600px] mx-auto px-[40px]"
        justify="center"
      >
        <Text variant={TEXT_STYLES.P} style={{ color }} className="!text-base">
          {`${copyrightYear} ${subfooterElements?.copyright}`}
        </Text>
        <span>
          <StyledLink
            href={subfooterElements?.createurDuSite.url}
            target={subfooterElements?.createurDuSite.target}
          >
            <Text
              style={{ color }}
              className="!text-base"
              variant={TEXT_STYLES.P}
            >
              {'&nbsp' + subfooterElements?.createurDuSite.title}
            </Text>
          </StyledLink>
        </span>
        <span onClick={openCookiehubBanner} className="cursor-pointer">
          <Text
            style={{ color }}
            className="!text-base"
            variant={TEXT_STYLES.P}
          >
            {'&nbsp' + subfooterElements?.texteLienCookies}
          </Text>
        </span>
      </Flex>
    </Box>
  );
};

export default Footer;
